import React from 'react';
import ActivitySection from './ActivitySection';
import SectionState from './SectionState';
import {
  addDays, dayMonth, londonDateKey, plural, weekdayIndex,
} from './lib/activityFormat';

export const CALENDAR_DAYS = 182;

/** Heat level for a day's items: 0 none, 1 one, 2 two or three, 3 four to six, 4 seven or more. */
export function heatLevel(items) {
  if (!items) return 0;
  if (items === 1) return 1;
  if (items <= 3) return 2;
  if (items <= 6) return 3;
  return 4;
}

/**
 * The last 182 days ending on `endKey`, oldest first, each with its items
 * (0 for a day the backend left out, since it sends non-zero days only).
 */
export function fillCalendar(days, endKey) {
  const byDate = new Map(days.map((d) => [d.date, d.items]));
  return Array.from({ length: CALENDAR_DAYS }, (_, i) => {
    const date = addDays(endKey, i - (CALENDAR_DAYS - 1));
    return { date, items: byDate.get(date) || 0 };
  });
}

const ByApp = ({ apps }) => {
  const max = Math.max(0, ...apps.map((a) => a.items));
  return (
    <div className="td-card">
      <h4 className="td-card__title">By app</h4>
      {max > 0 ? apps.map((a) => (
        <div key={a.key} className="td-act-appbar">
          <span>{a.label}</span>
          <span className="td-act-bar" aria-hidden="true"><i style={{ width: `${(a.items / max) * 100}%` }} /></span>
          <span className="td-act-num">{a.items}</span>
        </div>
      )) : <p className="td-act-meta">No completed work in this period.</p>}
    </div>
  );
};

const Calendar = ({ days, endKey }) => {
  const filled = fillCalendar(days, endKey);
  const active = filled.filter((d) => d.items > 0);
  const total = active.reduce((sum, d) => sum + d.items, 0);
  // Rows are Monday to Sunday: blank cells before the first day keep each row one weekday.
  const lead = weekdayIndex(filled[0].date);
  return (
    <div className="td-card">
      <h4 className="td-card__title">Last 26 weeks</h4>
      <div
        className="td-act-heat"
        role="img"
        aria-label={`Activity by day for the last 26 weeks: ${plural(active.length, 'active day')}, ${plural(total, 'item')} in all`}
      >
        {Array.from({ length: lead }, (_, i) => <i key={`lead-${i}`} className="td-act-heat__blank" />)}
        {filled.map((d) => (
          <i
            key={d.date}
            className={`td-act-heat__l${heatLevel(d.items)}`}
            title={`${dayMonth(d.date)}: ${plural(d.items, 'item')}`}
          />
        ))}
      </div>
      <div className="td-act-legend td-act-legend--heat">
        <span className="td-act-heat__l1">1 item</span>
        <span className="td-act-heat__l2">2–3</span>
        <span className="td-act-heat__l3">4–6</span>
        <span className="td-act-heat__l4">7+</span>
      </div>
      {active.length === 0 ? <p className="td-act-meta">No completed work in the last 26 weeks.</p> : null}
    </div>
  );
};

/**
 * The engagement grid: items per app, and the 26-week calendar. Loading,
 * empty and error stay on this block, so a failure here does not blank
 * whatever section renders it.
 */
export const EngagementBody = ({ state }) => {
  const data = state.status === 'ready' ? state.data : null;
  return (
    <>
      <SectionState state={state} what="engagement" />
      {data ? (
        <div className="td-act-grid td-act-grid--12">
          <ByApp apps={data.by_app} />
          <Calendar days={data.calendar.days} endKey={londonDateKey(data.generated_at)} />
        </div>
      ) : null}
    </>
  );
};

/**
 * Engagement, from GET …/activity/engagement: items per app for the period,
 * and a calendar of the last 26 weeks whatever the period. The calendar ends
 * on the London date the response was generated, so it never depends on the
 * viewer's clock.
 */
const EngagementSection = ({ state }) => (
  <ActivitySection
    id="engagement"
    title="Engagement"
    description="When and where the student works."
  >
    <EngagementBody state={state} />
  </ActivitySection>
);

export default EngagementSection;
